import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import { useSearchParams } from 'react-router-dom';

import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import SearchForm from '../../partials/actions/SearchForm';
import OffensiveEngagementsTable from '../../partials/offensive/OffensiveEngagementsTable';
import PaginationClassic from '../../components/PaginationClassic';

import CreateTestModal from '../../components/offensive/CreateTestModal';
import CreateWirelessEngagementModal from '../../components/offensive/CreateWirelessEngagementModal';

/**
 * The engagement list for the offensive module, and for wireless.
 *
 * Parameterised rather than copied: a wireless landing page is the same table
 * over a different subtype, and a second 300-line page would drift from this
 * one the first time either was touched.
 */
function OffensiveEngagements({
  subtypeIds = [1, 2, 3, 4],
  heading = 'Network & Applications',
  detailPath = '/offensive/operations',
  detailLabel = 'Testing',
  // Wireless engagements get their own form: the offensive one collects network
  // ranges, application URLs and orchestration settings, none of which a
  // wireless assessment has.
  EngagementModal = CreateTestModal,
}) {
  const [searchParams] = useSearchParams();
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [noResults, setNoResults] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [engagementModalOpen, setEngagementModalOpen] = useState(false);
  const [editingEngagement, setEditingEngagement] = useState(null);

  const [subservices, setSubservices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [userRole, setUserRole] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState([]);

  // Notification modal
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');

  const showNotification = (message, type = 'error') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setNotificationOpen(true);
    setTimeout(() => setNotificationOpen(false), 3500);
  };

  // Open modal if ?openModal=true in URL
  useEffect(() => {
    if (searchParams.get('openModal') === 'true') {
      setEngagementModalOpen(true);
    }
  }, [searchParams]);

  // Subservices offered by the create modal — the ones this page lists, so a
  // wireless page creates a wireless engagement rather than a pentest.
  useEffect(() => {
    const fetchSubservices = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${Config.APIBaseURL}/subservices/`, {
          headers: { access_token: `Bearer ${token}` },
          withCredentials: true,
        });
        const offensive = response.data.filter((sub) => subtypeIds.includes(sub.id));
        setSubservices(offensive);
      } catch (error) {
        console.error('Error fetching subservices:', error);
        setSubservices([
          { id: 1, name: 'Red Teaming', description: 'Full adversary simulation' },
          { id: 2, name: 'Network Intrusion Test', description: 'Traditional pentesting' },
          { id: 3, name: 'DAST', description: 'Dynamic application security testing' },
          { id: 4, name: 'SAST', description: 'Static application security testing' },
        ]);
      }
    };
    fetchSubservices();
  }, []);

  // Get user role on mount
  useEffect(() => {
    try {
      const role = JSON.parse(localStorage.getItem('role'));
      setUserRole(role);
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  }, []);

  // Fetch customers based on user's role
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${Config.APIBaseURL}/customers/`, {
          headers: { access_token: `Bearer ${token}` },
          withCredentials: true,
        });

        if (Array.isArray(response.data)) {
          setCustomers(
            response.data.map((customer) => ({
              id: customer.id,
              label: customer.short_name,
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        // Fallback for regular users
        if (userRole === 'user') {
          try {
            const userInfo = JSON.parse(localStorage.getItem('userinfo'));
            if (userInfo?.org && userInfo?.org_name) {
              setCustomers([{ id: userInfo.org, label: userInfo.org_name }]);
            }
          } catch (e) {
            /* ignore */
          }
        }
      }
    };

    fetchCustomers();
  }, [userRole]);

  const loadEngagements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${Config.APIBaseURL}/engagements/`, {
        headers: { access_token: `Bearer ${token}` },
        withCredentials: true,
      });
      if (response.data) {
        const offensive = response.data
          .filter((eng) => eng.subtype_id && subtypeIds.includes(eng.subtype_id))
          .map((eng) => ({
            ...eng,
            findings_count: eng.findings_count ?? eng.offensive_findings?.length ?? 0,
            status: eng.progress >= 100 ? 'finished'
              : eng.progress > 0 ? 'in progress'
              : (() => { try { const fc = JSON.parse(eng.offensive_scope?.ai_config || '{}'); return fc.pentagi_flow_ids?.length > 0 ? 'in progress' : (eng.status || 'not started'); } catch { return eng.status || 'not started'; } })(),
          }));
        setEngagements(offensive);
      }
    } catch (error) {
      console.error('Error loading offensive engagements:', error);
    }
    setLoading(false);
  };

  // Keyed on the subtype list, not on mount. The same component serves the
  // offensive and wireless lists, so switching between them does not remount it
  // — and a mount-only load left each page showing the other one's rows. The
  // routes also pass a key, which is belt and braces: either alone is enough,
  // and neither is obvious to whoever adds the third list.
  const subtypeKey = subtypeIds.join(',');
  useEffect(() => {
    loadEngagements();
  }, [subtypeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchEngagements = async (term) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${Config.APIBaseURL}/search_eng/${term}`, {
        headers: { access_token: `Bearer ${token}` },
        withCredentials: true,
      });
      if (response?.data?.length > 0) {
        const offensiveResults = response.data.filter(
          (eng) => eng.subtype_id && subtypeIds.includes(eng.subtype_id)
        );
        if (offensiveResults.length > 0) {
          setNoResults(false);
          setEngagements(offensiveResults);
        } else {
          setNoResults(true);
          setEngagements([]);
        }
      } else {
        setNoResults(true);
        setEngagements([]);
      }
    } catch (error) {
      console.error('Error searching engagements:', error);
      setNoResults(true);
      setEngagements([]);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    if (searchTerm.trim() !== '') searchEngagements(searchTerm);
  };

  const handleSelectedItems = (items) => setSelectedItems([...items]);

  const handleDeleteEngagement = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const engagementIds = Array.isArray(id) ? id : [id];
      const response = await axios.post(
        `${Config.APIBaseURL}/engagement/delete`,
        { engagements: engagementIds },
        {
          headers: {
            'Content-Type': 'application/json',
            access_token: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        setEngagements(engagements.filter((eng) => !engagementIds.includes(eng.id)));
        setIdsToDelete([]);
        setShowConfirmModal(false);
        showNotification('Engagement deleted successfully', 'success');
      }
    } catch (error) {
      console.error('Error deleting offensive engagement:', error);
      if (error.response?.status === 403) {
        showNotification('Not authorized. Only the owner or an admin can delete this engagement.');
      } else if (error.response?.status === 404) {
        showNotification('Engagement not found');
      } else {
        showNotification('Failed to delete engagement');
      }
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            {/* Page header */}
            <div className="sm:flex sm:justify-between sm:items-center mb-5">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl text-slate-800 dark:text-slate-100 font-bold">{heading}</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Manage red team, pentest, DAST and SAST engagements.
                </p>
              </div>
              <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                <SearchForm placeholder="Search" />
                <button
                  className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingEngagement(null);
                    setEngagementModalOpen(true);
                  }}
                >
                  <svg className="w-4 h-4 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                    <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                  </svg>
                  <span className="xs:block ml-2">New Engagement</span>
                </button>
              </div>
            </div>

            {/* Engagements table */}
            <OffensiveEngagementsTable
              title={heading}
              detailPath={detailPath}
              detailLabel={detailLabel}
              engagements={engagements}
              selectedItems={handleSelectedItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onDeleteEngagement={handleDeleteEngagement}
              onCreateNew={() => { setEditingEngagement(null); setEngagementModalOpen(true); }}
              onEditEngagement={(eng) => { setEditingEngagement(eng); setEngagementModalOpen(true); }}
              customers={customers}
              subservices={subservices}
            />

            <div className="mt-8">
              <PaginationClassic
                currentPage={currentPage}
                totalItems={engagements ? engagements.length : 0}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Create / Edit Engagement Modal */}
      <EngagementModal
        open={engagementModalOpen}
        onClose={() => { setEngagementModalOpen(false); setEditingEngagement(null); }}
        onCreated={loadEngagements}
        onNotify={showNotification}
        userRole={userRole}
        customers={customers}
        subservices={subservices}
        engagement={editingEngagement}
      />

      {/* Notification Toast */}
      {notificationOpen && (
        <div className="fixed top-6 right-6 z-[70]">
          <div className="flex items-center w-full max-w-xs p-4 text-gray-500 bg-white rounded-lg shadow-lg dark:text-gray-400 dark:bg-gray-700" role="alert">
            <div
              className={`inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg ${
                notificationType === 'success'
                  ? 'text-green-500 bg-green-100 dark:bg-green-800 dark:text-green-200'
                  : 'text-red-500 bg-red-100 dark:bg-red-800 dark:text-red-200'
              }`}
            >
              {notificationType === 'success' ? (
                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z" />
                </svg>
              )}
            </div>
            <div className="ms-3 text-sm text-slate-800 dark:text-slate-300">{notificationMessage}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OffensiveEngagements;
